import React, { useRef, useState } from 'react';
import { UploadCloud, Loader2, Trash2, ImageIcon, CheckCircle2 } from 'lucide-react';
import { api } from '../../context/AuthContext';

const ALLOWED = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_INPUT = 10 * 1024 * 1024; // raw file guard before compression

// Canvas-based compression: resize to max 1600px, convert to WebP (JPEG fallback)
async function compressImage(file) {
  const dataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = dataUrl;
  });
  const MAX_W = 1600;
  const scale = Math.min(1, MAX_W / img.width);
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  canvas.getContext('2d').drawImage(img, 0, 0, w, h);
  let out = canvas.toDataURL('image/webp', 0.8);
  if (!out.startsWith('data:image/webp')) out = canvas.toDataURL('image/jpeg', 0.8);
  return { out, width: img.width, height: img.height };
}

export const CoverUpload = ({ image, setImage }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [drag, setDrag] = useState(false);
  const [info, setInfo] = useState(null);

  const handleFile = async (file) => {
    if (!file) return;
    setError(null);
    if (!ALLOWED.includes(file.type)) { setError('Format non supporté. Utilisez JPG, PNG ou WebP.'); return; }
    if (file.size > MAX_INPUT) { setError('Image trop lourde (max 10 Mo avant compression).'); return; }
    setBusy(true);
    try {
      const { out, width, height } = await compressImage(file);
      const bytes = Math.round((out.length * 3) / 4);
      if (bytes > 2 * 1024 * 1024) { setError('Image trop lourde même après compression (max 2 Mo).'); return; }
      const { data } = await api.post('/uploads/cover', { data: out });
      setImage(data.url);
      setInfo({ size: data.size, width, height, format: out.startsWith('data:image/webp') ? 'WebP' : 'JPEG' });
    } catch (e) {
      setError(e?.response?.data?.detail || "Échec de l'envoi de l'image.");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        hidden
        data-testid="cover-upload-input"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
      {!image ? (
        <button
          type="button"
          data-testid="cover-upload-zone"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files?.[0]); }}
          className={`mt-1.5 w-full rounded-xl border-2 border-dashed py-8 px-4 text-center transition-colors ${drag ? 'border-[#d97757] bg-[#d97757]/5' : 'border-black/10 bg-[#f5f4ef] hover:border-[#d97757]/50'}`}
        >
          {busy ? (
            <Loader2 className="w-7 h-7 mx-auto animate-spin text-neutral-400" />
          ) : (
            <UploadCloud className="w-7 h-7 mx-auto text-neutral-400" />
          )}
          <div className="mt-2 text-[13px] font-medium text-neutral-700">
            {busy ? 'Compression et envoi…' : 'Glissez-déposez votre image ici'}
          </div>
          {!busy && (
            <div className="mt-0.5 text-[11.5px] text-neutral-500">
              ou touchez pour choisir depuis la galerie / l'appareil photo · JPG, PNG, WebP · max 2 Mo
            </div>
          )}
        </button>
      ) : (
        <div className="mt-1.5 flex items-center justify-between gap-2 rounded-xl bg-[#f5f4ef] px-3 py-2" data-testid="cover-upload-done">
          <div className="flex items-center gap-2 min-w-0 text-[12.5px] text-neutral-700">
            <CheckCircle2 className="w-4 h-4 text-[#128C4A] shrink-0" />
            <span className="truncate">
              Image ajoutée{info ? ` · ${info.format} · ${Math.round(info.size / 1024)} Ko` : ''}
            </span>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              data-testid="cover-change-btn"
              onClick={() => inputRef.current?.click()}
              className="text-[12px] font-medium text-neutral-600 hover:text-[#1a1d1a] px-2 py-1 rounded-lg hover:bg-black/5 transition-colors flex items-center gap-1"
            >
              <ImageIcon className="w-3.5 h-3.5" /> Changer
            </button>
            <button
              type="button"
              data-testid="cover-remove-btn"
              onClick={() => { setImage(''); setInfo(null); }}
              className="text-[12px] font-medium text-red-500 hover:text-red-600 px-2 py-1 rounded-lg hover:bg-red-50 transition-colors flex items-center gap-1"
            >
              <Trash2 className="w-3.5 h-3.5" /> Supprimer
            </button>
          </div>
        </div>
      )}
      {error && (
        <div className="mt-2 text-[12px] text-red-600 bg-red-50 rounded-lg px-3 py-2" data-testid="cover-upload-error">{error}</div>
      )}
    </div>
  );
};
